import React from 'react';
import { practicalInfo, siteConfig } from '../data';
import { MapPin, Clock, CreditCard, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

const Praktisk = () => {
  return (
    <section id="praktisk" className="py-24 md:py-32 bg-orange-50/60 overflow-hidden relative">
      {/* Decorative side line */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-32 bg-stone-300"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-6 block font-bold">
              Information
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-stone-900 mb-8 leading-tight">
              Praktisk info & <br />
              <span className="font-serif italic text-stone-500 lowercase tracking-normal">Booking</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
            
            {/* Location Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white p-10 md:p-12 shadow-sm flex flex-col border border-stone-100"
            >
              <div className="mb-10">
                <div className="w-14 h-14 bg-stone-50 flex items-center justify-center mb-8 border border-stone-100">
                  <MapPin size={24} strokeWidth={1.2} className="text-stone-800" />
                </div>
                <h3 className="text-2xl font-light text-stone-900 mb-6 tracking-tight">{practicalInfo.location.title}</h3>
                <p className="text-stone-600 font-light mb-8 leading-relaxed text-base">
                  {practicalInfo.location.description}
                </p>
              </div>
              
              <div className="mt-auto space-y-4">
                <div>
                  <p className="text-stone-400 text-[10px] uppercase tracking-widest mb-1">Adresse</p>
                  <p className="text-stone-900 font-medium">{practicalInfo.location.address}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-[10px] uppercase tracking-widest mb-1">Adgang</p>
                  <p className="text-sm text-stone-600 italic">
                    {practicalInfo.location.access}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Pricing Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-10 md:p-12 shadow-sm flex flex-col border border-stone-100"
            >
              <div className="mb-10">
                <div className="w-14 h-14 bg-stone-50 flex items-center justify-center mb-8 border border-stone-100">
                  <CreditCard size={24} strokeWidth={1.2} className="text-stone-800" />
                </div>
                <h3 className="text-2xl font-light text-stone-900 mb-8 tracking-tight">Prisliste & Betaling</h3>
                <div className="space-y-6">
                  {practicalInfo.booking.pricing.map((item, index) => (
                    <div key={index} className="flex justify-between items-start border-b border-stone-50 pb-5 last:border-0 last:pb-0">
                      <div className="pr-4">
                        <span className="text-stone-700 font-light text-base leading-tight block">{item.service}</span>
                        {item.duration && (
                          <span className="text-stone-500 font-light text-sm block mt-1">{item.duration}</span>
                        )}
                      </div>
                      <span className="text-stone-900 font-medium whitespace-nowrap pt-0.5">{item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-auto pt-8 border-t border-stone-100">
                <div className="flex items-center space-x-3 text-[10px] text-stone-400 uppercase tracking-[0.2em] font-bold">
                  <Clock size={14} strokeWidth={1.5} />
                  <span>MobilePay eller kontant</span>
                </div>
              </div>
            </motion.div>

            {/* Booking Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-[#b8a58d] p-10 md:p-12 shadow-xl flex flex-col text-stone-50"
            >
              <div className="mb-10">
                <div className="w-14 h-14 bg-white/10 flex items-center justify-center mb-8">
                  <Calendar size={24} strokeWidth={1.2} className="text-stone-50" />
                </div>
                <h3 className="text-2xl font-light mb-6 tracking-tight text-white">{practicalInfo.booking.title}</h3>
                <p className="text-stone-300 font-light mb-10 leading-relaxed text-base">
                  {practicalInfo.booking.description}
                </p>
              </div>

              <div className="mt-auto">
                <a
                  href={siteConfig.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between w-full bg-white text-stone-900 px-8 py-5 text-xs tracking-[0.2em] uppercase font-bold hover:bg-stone-100 transition-all duration-300 shadow-lg"
                >
                  <span>Book online nu</span>
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    →
                  </motion.span>
                </a>
                <p className="text-[10px] text-stone-400 mt-8 text-center uppercase tracking-[0.3em] font-medium">
                  Jeg glæder mig til at se dig
                </p>
              </div>
            </motion.div>

          </div>
      </div>
    </section>
  );
};

export default Praktisk;
